import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast, Button, Spinner, Modal } from '../../components/ds';
import api from '../../api/client';
import TargetingForm from '../../components/TargetingForm/TargetingForm';
import './CreateAdWizard.css';

const getDatePart = (dt) => dt.split('T')[0] || '';
const getTimePart = (dt, fallback = '00:00') => dt.split('T')[1] || fallback;
const setDatePart = (dt, date, timeFallback = '00:00') => `${date}T${getTimePart(dt, timeFallback)}`;
const setTimePart = (dt, time) => `${dt.split('T')[0] || new Date().toISOString().split('T')[0]}T${time}`;

const handleNumericInput = (setter) => (e) => {
  const raw = e.target.value.replace(/,/g, '');
  if (raw === '' || /^\d+$/.test(raw)) setter(raw);
};
const formatNumber = (val) => (val ? Number(val).toLocaleString() : '');

const EMPTY_DAY_PARTS = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };


export default function CreateAdWizard() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [searchParams] = useSearchParams();

  // Resume mode: read from URL query params
  const editId = searchParams.get('edit');
  const resumeStep = searchParams.get('step');
  const isResumeMode = !!editId;

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isResumeMode);
  const [adID, setAdID] = useState(null);

  // Step 1: Application + Placement Type
  const [appLoading, setAppLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [placementTypes, setPlacementTypes] = useState([]);
  const [selectedApplicationID, setSelectedApplicationID] = useState('');
  const [selectedPlacementTypeID, setSelectedPlacementTypeID] = useState('');
  const [resumePlacementType, setResumePlacementType] = useState('');

  // Step 2: Basic Info
  const [adName, setAdName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [totalImpressions, setTotalImpressions] = useState('');
  const [dailyImpressions, setDailyImpressions] = useState('');
  const [enableDailyLimit, setEnableDailyLimit] = useState(false);

  // Step 3: Targeting (v2)
  const [enabledFeatures, setEnabledFeatures] = useState(null);

  // Frequency capping
  const [freqTotal, setFreqTotal] = useState('');
  const [freqDaily, setFreqDaily] = useState('');

  // Day parts (grid-based)
  const [dayPartsActive, setDayPartsActive] = useState(false);
  const [dayPartsValue, setDayPartsValue] = useState({ ...EMPTY_DAY_PARTS });

  // Keywords (array of strings)
  const [includeKeywords, setIncludeKeywords] = useState([]);
  const [excludeKeywords, setExcludeKeywords] = useState([]);

  // Country & Language
  const [countryWhitelist, setCountryWhitelist] = useState([]);
  const [languageWhitelist, setLanguageWhitelist] = useState([]);

  // Demographic
  const [genderWhitelist, setGenderWhitelist] = useState([]);
  const [ageWhitelist, setAgeWhitelist] = useState([]);

  // Audience
  const [audienceWhitelist, setAudienceWhitelist] = useState([]);
  const [audienceBlacklist, setAudienceBlacklist] = useState([]);

  // Card-level enabled toggles
  const [freqEnabled, setFreqEnabled] = useState(false);
  const [keywordsEnabled, setKeywordsEnabled] = useState(false);
  const [regionEnabled, setRegionEnabled] = useState(false);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [audienceEnabled, setAudienceEnabled] = useState(false);

  // Cancel modal
  const [showCancelModal, setShowCancelModal] = useState(false);

  const companyID = localStorage.getItem('portal_company_id');

  const DRAFT_KEY = 'createAdWizard_draft';

  // Collect all form data into one object for draft saving
  const collectDraftData = useCallback(() => ({
    step: currentStep,
    application_id: selectedApplicationID,
    placement_type_id: selectedPlacementTypeID,
    name: adName,
    start_time: startTime,
    end_time: endTime,
    total_impressions: totalImpressions,
    daily_impressions: dailyImpressions,
    enable_daily_limit: enableDailyLimit,
    freq_enabled: freqEnabled,
    freq_total: freqTotal,
    freq_daily: freqDaily,
    day_parts_active: dayPartsActive,
    day_parts_value: dayPartsValue,
    keywords_enabled: keywordsEnabled,
    include_keywords: includeKeywords,
    exclude_keywords: excludeKeywords,
    region_enabled: regionEnabled,
    country_whitelist: countryWhitelist,
    language_whitelist: languageWhitelist,
    demo_enabled: demoEnabled,
    gender_whitelist: genderWhitelist,
    age_whitelist: ageWhitelist,
    audience_enabled: audienceEnabled,
    audience_whitelist: audienceWhitelist,
    audience_blacklist: audienceBlacklist,
    saved_at: new Date().toISOString(),
  }), [currentStep, selectedApplicationID, selectedPlacementTypeID, adName,
    startTime, endTime, totalImpressions, dailyImpressions, enableDailyLimit,
    freqEnabled, freqTotal, freqDaily, dayPartsActive, dayPartsValue,
    keywordsEnabled, includeKeywords, excludeKeywords, regionEnabled,
    countryWhitelist, languageWhitelist, demoEnabled, genderWhitelist,
    ageWhitelist, audienceEnabled, audienceWhitelist, audienceBlacklist]);

  const saveDraft = useCallback(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(collectDraftData()));
    } catch (e) { /* ignore quota errors */ }
  }, [collectDraftData]);

  // Load draft on mount (only if not in resume mode)
  useEffect(() => {
    if (isResumeMode) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.step) setCurrentStep(d.step);
      if (d.application_id) setSelectedApplicationID(d.application_id);
      if (d.placement_type_id) setSelectedPlacementTypeID(d.placement_type_id);
      if (d.name) setAdName(d.name);
      if (d.start_time) setStartTime(d.start_time);
      if (d.end_time) setEndTime(d.end_time);
      if (d.total_impressions) setTotalImpressions(d.total_impressions);
      if (d.daily_impressions) setDailyImpressions(d.daily_impressions);
      if (d.enable_daily_limit) setEnableDailyLimit(d.enable_daily_limit);
      if (d.freq_enabled) setFreqEnabled(d.freq_enabled);
      if (d.freq_total) setFreqTotal(d.freq_total);
      if (d.freq_daily) setFreqDaily(d.freq_daily);
      if (d.day_parts_active) setDayPartsActive(d.day_parts_active);
      if (d.day_parts_value) setDayPartsValue(d.day_parts_value);
      if (d.keywords_enabled) setKeywordsEnabled(d.keywords_enabled);
      if (d.include_keywords) setIncludeKeywords(d.include_keywords);
      if (d.exclude_keywords) setExcludeKeywords(d.exclude_keywords);
      if (d.region_enabled) setRegionEnabled(d.region_enabled);
      if (d.country_whitelist) setCountryWhitelist(d.country_whitelist);
      if (d.language_whitelist) setLanguageWhitelist(d.language_whitelist);
      if (d.demo_enabled) setDemoEnabled(d.demo_enabled);
      if (d.gender_whitelist) setGenderWhitelist(d.gender_whitelist);
      if (d.age_whitelist) setAgeWhitelist(d.age_whitelist);
      if (d.audience_enabled) setAudienceEnabled(d.audience_enabled);
      if (d.audience_whitelist) setAudienceWhitelist(d.audience_whitelist);
      if (d.audience_blacklist) setAudienceBlacklist(d.audience_blacklist);
    } catch (e) { /* ignore parse errors */ }
  }, []); // eslint-disable-line

  // Auto-save debounced (500ms)
  const debouncedSave = useMemo(() => {
    let timer;
    const fn = () => { clearTimeout(timer); timer = setTimeout(() => saveDraft(), 500); };
    fn.cancel = () => clearTimeout(timer);
    return fn;
  }, [saveDraft]);

  useEffect(() => {
    if (isResumeMode) return;
    debouncedSave();
    return () => debouncedSave.cancel();
  }, [collectDraftData, debouncedSave, isResumeMode]);

  // Cancel handler
  const handleCancel = () => setShowCancelModal(true);
  const confirmCancel = () => {
    localStorage.removeItem(DRAFT_KEY);
    navigate('/ads');
  };

  // Save draft handler (manual)
  const handleSaveDraft = () => {
    saveDraft();
    addToast('草稿已儲存', 'success');
  };

  // Helper: parse RFC3339 datetime to "YYYY-MM-DDTHH:mm" for form inputs
  const parseDateTime = (rfc3339) => {
    if (!rfc3339) return '';
    const d = new Date(rfc3339);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };


  // Load draft data when in resume mode
  useEffect(() => {
    if (!isResumeMode || !companyID) return;

    const loadDraft = async () => {
      setInitialLoading(true);
      try {
        const ad = await api.getPortalAd(editId);

        // Set adID (PAC resources already created)
        setAdID(ad.id);

        // Step 1 data (read-only in resume) — fetch display name
        const types = await api.getPortalPlacementTypes();
        const matched = (types || []).find(t => t.name === ad.placement_type);
        setResumePlacementType(
          matched ? `${matched.display_name} (${matched.platform})` : (ad.placement_type || '')
        );

        // Step 2 data
        setAdName(ad.name || '');
        setStartTime(parseDateTime(ad.start_time));
        setEndTime(parseDateTime(ad.end_time));
        setTotalImpressions(ad.total_impressions ? String(ad.total_impressions) : '');
        setDailyImpressions(ad.daily_impressions ? String(ad.daily_impressions) : '');
        setEnableDailyLimit(ad.daily_impressions > 0);

        // Step 3 data (targeting v2)
        if (ad.targeting) {
          const t = typeof ad.targeting === 'string' ? JSON.parse(ad.targeting) : ad.targeting;

          // Frequency capping
          if (t.frequency_capping_total || t.frequency_capping_daily) {
            setFreqEnabled(true);
            setFreqTotal(t.frequency_capping_total ? String(t.frequency_capping_total) : '');
            setFreqDaily(t.frequency_capping_daily ? String(t.frequency_capping_daily) : '');
          }

          // Day parts (v2 format: object with day keys)
          if (t.day_parts && typeof t.day_parts === 'object' && !Array.isArray(t.day_parts)) {
            setDayPartsActive(!!t.day_parts.isActive);
            const dpValue = { ...EMPTY_DAY_PARTS };
            for (const dayKey of Object.keys(EMPTY_DAY_PARTS)) {
              if (Array.isArray(t.day_parts[dayKey])) {
                dpValue[dayKey] = t.day_parts[dayKey];
              }
            }
            setDayPartsValue(dpValue);
          } else if (t.day_parts_switch !== undefined) {
            setDayPartsActive(!!t.day_parts_switch);
          }

          // Keywords
          if (t.keywords) {
            const inc = t.keywords.include || [];
            const exc = t.keywords.exclude || [];
            if (inc.length || exc.length) setKeywordsEnabled(true);
            setIncludeKeywords(inc);
            setExcludeKeywords(exc);
          } else if (t.include || t.exclude) {
            const inc = t.include || [];
            const exc = t.exclude || [];
            if (inc.length || exc.length) setKeywordsEnabled(true);
            setIncludeKeywords(inc);
            setExcludeKeywords(exc);
          }

          // Country & Language
          const hasCountry = t.country && !t.country.allow_all && (t.country.whitelist || []).length > 0;
          const hasLanguage = t.language && !t.language.allow_all && (t.language.whitelist || []).length > 0;
          if (hasCountry || hasLanguage) setRegionEnabled(true);
          if (t.country) setCountryWhitelist(t.country.whitelist || []);
          if (t.language) setLanguageWhitelist(t.language.whitelist || []);

          // Demographic
          const hasGender = (t.gender?.whitelist || []).length > 0;
          const hasAge = (t.age?.whitelist || []).length > 0;
          if (hasGender || hasAge) setDemoEnabled(true);
          if (t.gender) setGenderWhitelist(t.gender.whitelist || []);
          if (t.age) setAgeWhitelist(t.age.whitelist || []);

          // Audience
          const hasAudience = (t.audience?.whitelist || []).length > 0 || (t.audience?.blacklist || []).length > 0;
          if (hasAudience) setAudienceEnabled(true);
          if (t.audience) {
            setAudienceWhitelist(t.audience.whitelist || []);
            setAudienceBlacklist(t.audience.blacklist || []);
          }
        }

        // Load features for Step 3
        const featuresData = await api.getPortalFeatures();
        setEnabledFeatures(featuresData.features?.targeting || {});

        // Jump to the target step
        const targetStep = parseInt(resumeStep) || ad.current_step || 2;
        setCurrentStep(targetStep);
      } catch (err) {
        addToast('載入草稿失敗：' + err.message, 'error');
        navigate('/ads');
      } finally {
        setInitialLoading(false);
      }
    };

    loadDraft();
  }, [editId]); // eslint-disable-line

  // Fetch data for Step 1 (only in new ad mode)
  useEffect(() => {
    if (!companyID) {
      navigate('/login');
      return;
    }
    if (!isResumeMode && currentStep === 1) {
      fetchApplications();
      fetchPlacementTypes();
    }
  }, [currentStep, companyID, navigate]); // eslint-disable-line

  const fetchApplications = async () => {
    setAppLoading(true);
    try {
      const data = await api.getPortalApplications(companyID);
      setApplications(data || []);
      if (data && data.length > 0 && !selectedApplicationID) {
        setSelectedApplicationID(data[0].uuid);
      }
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setAppLoading(false);
    }
  };

  const fetchPlacementTypes = async () => {
    try {
      const data = await api.getPortalPlacementTypes();
      setPlacementTypes(data || []);
      if (data && data.length > 0 && !selectedPlacementTypeID) {
        setSelectedPlacementTypeID(String(data[0].id));
      }
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleStep1Next = () => {
    if (!selectedApplicationID || !selectedPlacementTypeID) {
      addToast('請選擇 Application 和 Placement Type', 'error');
      return;
    }
    if (!startTime) {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      setStartTime(`${todayStr}T00:00`);
    }
    if (!endTime) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      setEndTime(`${tomorrowStr}T23:59`);
    }
    setCurrentStep(2);
  };

  const handleStep2Next = async () => {
    if (!adName || !startTime || !endTime || !totalImpressions) {
      addToast('請填寫所有必填欄位', 'error');
      return;
    }

    setLoading(true);
    try {
      if (isResumeMode && adID) {
        // Resume mode: update existing ad via PUT
        await api.updatePortalAd(adID, {
          ad_name: adName,
          start_time: startTime,
          end_time: endTime,
          total_impressions: parseInt(totalImpressions),
          daily_impressions: dailyImpressions ? parseInt(dailyImpressions) : 0,
        });
        // Load features for Step 3 if not already loaded
        if (!enabledFeatures) {
          const featuresData = await api.getPortalFeatures();
          setEnabledFeatures(featuresData.features?.targeting || {});
        }
      } else {
        // New ad: Create the ad (campaign + placement + order + adgroup)
        const result = await api.createPortalAd({
          application_id: selectedApplicationID,
          placement_type_id: parseInt(selectedPlacementTypeID),
          company_id: companyID,
          ad_name: adName,
          start_time: startTime,
          end_time: endTime,
          total_impressions: parseInt(totalImpressions),
          daily_impressions: dailyImpressions ? parseInt(dailyImpressions) : 0,
        });
        setAdID(result.ad_id);

        // Fetch account features for Step 3
        const featuresData = await api.getPortalFeatures();
        setEnabledFeatures(featuresData.features?.targeting || {});
      }

      setCurrentStep(3);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStep3Save = async () => {
    setLoading(true);
    try {
      const payload = {
        frequency_capping_total: freqEnabled && freqTotal ? parseInt(freqTotal) : 0,
        frequency_capping_daily: freqEnabled && freqDaily ? parseInt(freqDaily) : 0,
        day_parts: {
          isActive: dayPartsActive,
          mon: dayPartsValue.mon || [],
          tue: dayPartsValue.tue || [],
          wed: dayPartsValue.wed || [],
          thu: dayPartsValue.thu || [],
          fri: dayPartsValue.fri || [],
          sat: dayPartsValue.sat || [],
          sun: dayPartsValue.sun || [],
        },
        keywords: {
          include: keywordsEnabled ? includeKeywords : [],
          exclude: keywordsEnabled ? excludeKeywords : [],
        },
        language: {
          allow_all: !regionEnabled,
          whitelist: regionEnabled ? languageWhitelist : [],
        },
        country: {
          allow_all: !regionEnabled,
          whitelist: regionEnabled ? countryWhitelist : [],
        },
        gender: {
          allow_all: !demoEnabled || genderWhitelist.length === 0,
          whitelist: demoEnabled ? genderWhitelist : [],
        },
        age: {
          allow_all: !demoEnabled || ageWhitelist.length === 0,
          whitelist: demoEnabled ? ageWhitelist : [],
        },
        audience: {
          whitelist: audienceEnabled ? audienceWhitelist : [],
          blacklist: audienceEnabled ? audienceBlacklist : [],
        },
      };

      await api.updatePortalAdTargeting(adID, payload);

      localStorage.removeItem(DRAFT_KEY);
      addToast(isResumeMode ? '廣告設定完成！' : '廣告建立成功！', 'success');
      navigate('/ads');
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipTargeting = () => {
    localStorage.removeItem(DRAFT_KEY);
    addToast(isResumeMode ? '廣告設定完成！' : '廣告建立成功！', 'success');
    navigate('/ads');
  };


  if (initialLoading) {
    return (
      <div className="create-ad-wizard">
        <div className="wizard-header">
          <h1>載入草稿中...</h1>
        </div>
        <div className="wizard-content">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-ad-wizard">
      <div className="wizard-header">
        <h1>{isResumeMode ? '繼續編輯廣告' : '建立廣告'}</h1>
        <div className="wizard-steps">
          <div
            className={`step ${currentStep >= 1 ? 'active' : ''} ${isResumeMode || currentStep > 1 ? 'clickable' : ''}`}
            onClick={() => (isResumeMode || currentStep > 1) && setCurrentStep(1)}
          >
            <div className="step-number">1</div>
            <div className="step-label">選擇版位</div>
          </div>
          <div className="step-line"></div>
          <div
            className={`step ${currentStep >= 2 ? 'active' : ''} ${isResumeMode || currentStep > 2 ? 'clickable' : ''}`}
            onClick={() => (isResumeMode || currentStep > 2) && setCurrentStep(2)}
          >
            <div className="step-number">2</div>
            <div className="step-label">基本資訊</div>
          </div>
          <div className="step-line"></div>
          <div
            className={`step ${currentStep >= 3 ? 'active' : ''} ${isResumeMode && currentStep > 3 ? 'clickable' : ''}`}
            onClick={() => isResumeMode && currentStep > 3 && setCurrentStep(3)}
          >
            <div className="step-number">3</div>
            <div className="step-label">Targeting</div>
          </div>
        </div>
      </div>

      <div className="wizard-content">
        {currentStep === 1 && !isResumeMode && (
          <div className="step-1">
            <h2>選擇 Application 和版位類型</h2>
            <p className="step-description">
              選擇後將無法更改，請確認後再進入下一步
            </p>

            {appLoading ? (
              <div className="loading"><Spinner /></div>
            ) : applications.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
                此公司尚未設定 Application，請聯繫管理員
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Application（媒體/網站）</label>
                  <select
                    value={selectedApplicationID}
                    onChange={(e) => setSelectedApplicationID(e.target.value)}
                    required
                  >
                    <option value="">請選擇 Application</option>
                    {applications.map((app) => (
                      <option key={app.uuid} value={app.uuid}>
                        {app.app_name} ({app.os_type})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Placement Type（版位類型）</label>
                  <select
                    value={selectedPlacementTypeID}
                    onChange={(e) => setSelectedPlacementTypeID(e.target.value)}
                    required
                  >
                    <option value="">請選擇 Placement Type</option>
                    {placementTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.display_name} ({type.platform})
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="wizard-actions">
              <Button variant="secondary" onClick={handleCancel}>取消</Button>
              <div className="wizard-actions-right">
                <Button variant="secondary" onClick={handleSaveDraft}>儲存草稿</Button>
                <Button onClick={handleStep1Next} disabled={appLoading || applications.length === 0}>
                  下一步
                </Button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 1 && isResumeMode && (
          <div className="step-1">
            <h2>版位資訊（已鎖定）</h2>
            <p className="step-description">
              版位已選定，無法更改
            </p>

            <div className="form-group">
              <label>Placement Type（版位類型）</label>
              <div className="readonly-value">{resumePlacementType || '-'}</div>
            </div>

            <div className="wizard-actions">
              <Button variant="secondary" onClick={handleCancel}>取消</Button>
              <div className="wizard-actions-right">
                <Button onClick={() => setCurrentStep(2)}>
                  下一步
                </Button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="step-2">
            <h2>基本資訊</h2>

            <div className="form-group">
              <label>廣告名稱 *</label>
              <input
                type="text"
                value={adName}
                onChange={(e) => setAdName(e.target.value)}
                placeholder="輸入廣告名稱"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>走期開始 *</label>
                <div className="datetime-inputs">
                  <input
                    type="date"
                    value={getDatePart(startTime)}
                    onChange={(e) => setStartTime(setDatePart(startTime, e.target.value))}
                    required
                  />
                  <input
                    type="text"
                    className="time-input"
                    value={getTimePart(startTime)}
                    onChange={(e) => setStartTime(setTimePart(startTime, e.target.value))}
                    placeholder="00:00"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength={5}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>走期結束 *</label>
                <div className="datetime-inputs">
                  <input
                    type="date"
                    value={getDatePart(endTime)}
                    onChange={(e) => setEndTime(setDatePart(endTime, e.target.value, '23:59'))}
                    required
                  />
                  <input
                    type="text"
                    className="time-input"
                    value={getTimePart(endTime, '23:59')}
                    onChange={(e) => setEndTime(setTimePart(endTime, e.target.value))}
                    placeholder="23:59"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength={5}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>總曝光數 *</label>
                <input
                  type="text"
                  value={formatNumber(totalImpressions)}
                  onChange={handleNumericInput(setTotalImpressions)}
                  placeholder="10,000"
                  required
                />
              </div>
              <div className="form-group">
                <label className="toggle-label" style={{ marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={enableDailyLimit}
                    onChange={(e) => {
                      setEnableDailyLimit(e.target.checked);
                      if (!e.target.checked) setDailyImpressions('');
                    }}
                  />
                  <span>設定日曝光上限</span>
                </label>
                <input
                  type="text"
                  value={formatNumber(dailyImpressions)}
                  onChange={handleNumericInput(setDailyImpressions)}
                  placeholder="1,000"
                  disabled={!enableDailyLimit}
                />
              </div>
            </div>

            <div className="wizard-actions">
              <Button variant="secondary" onClick={handleCancel}>取消</Button>
              <div className="wizard-actions-right">
                <Button variant="secondary" onClick={handleSaveDraft}>儲存草稿</Button>
                <Button variant="secondary" onClick={() => setCurrentStep(1)}>
                  上一步
                </Button>
                <Button onClick={handleStep2Next} disabled={loading}>
                  {loading ? '處理中...' : '下一步'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && enabledFeatures && (
          <div className="step-3">
            <TargetingForm
              enabledFeatures={enabledFeatures}
              freqEnabled={freqEnabled} onFreqEnabledChange={setFreqEnabled}
              freqTotal={freqTotal} onFreqTotalChange={setFreqTotal}
              freqDaily={freqDaily} onFreqDailyChange={setFreqDaily}
              keywordsEnabled={keywordsEnabled} onKeywordsEnabledChange={setKeywordsEnabled}
              includeKeywords={includeKeywords} onIncludeKeywordsChange={setIncludeKeywords}
              excludeKeywords={excludeKeywords} onExcludeKeywordsChange={setExcludeKeywords}
              regionEnabled={regionEnabled} onRegionEnabledChange={setRegionEnabled}
              countryWhitelist={countryWhitelist} onCountryWhitelistChange={setCountryWhitelist}
              languageWhitelist={languageWhitelist} onLanguageWhitelistChange={setLanguageWhitelist}
              demoEnabled={demoEnabled} onDemoEnabledChange={setDemoEnabled}
              genderWhitelist={genderWhitelist} onGenderWhitelistChange={setGenderWhitelist}
              ageWhitelist={ageWhitelist} onAgeWhitelistChange={setAgeWhitelist}
              dayPartsActive={dayPartsActive} onDayPartsActiveChange={setDayPartsActive}
              dayPartsValue={dayPartsValue} onDayPartsValueChange={setDayPartsValue}
              audienceEnabled={audienceEnabled} onAudienceEnabledChange={setAudienceEnabled}
              audienceWhitelist={audienceWhitelist} onAudienceWhitelistChange={setAudienceWhitelist}
              audienceBlacklist={audienceBlacklist} onAudienceBlacklistChange={setAudienceBlacklist}
            />

            <div className="wizard-actions">
              <Button variant="secondary" onClick={handleCancel}>取消</Button>
              <div className="wizard-actions-right">
                <Button variant="secondary" onClick={handleSaveDraft}>儲存草稿</Button>
                <Button variant="secondary" onClick={() => setCurrentStep(2)}>上一步</Button>
                <Button variant="secondary" onClick={handleSkipTargeting}>跳過</Button>
                <Button onClick={handleStep3Save} disabled={loading}>
                  {loading ? '儲存中...' : '完成建立'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showCancelModal && (
        <Modal
          title="取消建立廣告"
          onClose={() => setShowCancelModal(false)}
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setShowCancelModal(false)}>繼續編輯</Button>
              <Button onClick={confirmCancel}>確定取消</Button>
            </div>
          }
        >
          <p>確定要取消嗎？未儲存的變更將會遺失。</p>
        </Modal>
      )}
    </div>
  );
}
